// Core
import {
  Box,
  Image,
  Divider,
  Button,
  Grid,
  Text,
  Flex,
  useColorModeValue,
  useBreakpointValue,
  useColorMode,
} from '@chakra-ui/react';
import { useHistory, useParams } from 'react-router-dom';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { LuInstagram } from 'react-icons/lu';
import { RiTwitterXFill } from 'react-icons/ri';
import { MdFacebook } from 'react-icons/md';
import { FaYoutube, FaTiktok } from 'react-icons/fa';
import { FaTwitch } from 'react-icons/fa6';

// Assets
import { formatDateTime, parseFullDate } from 'services/Utils';
import { apiService } from 'services/ApiService';
import placeholderGiveaways from 'assets/img/placeholder-giveaways.jpg';
import descriptionImage from 'assets/img/glow-8.png';
import prizesImage from 'assets/img/details/prizes.png';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import { ReactComponent as LinesWhite } from 'assets/svg/lines-white.svg';
import { ReactComponent as Prize1 } from 'assets/svg/prizes/prize-1.svg';
import { ReactComponent as Prize2 } from 'assets/svg/prizes/prize-2.svg';
import { ReactComponent as Prize3 } from 'assets/svg/prizes/prize-3.svg';
import { ReactComponent as PlaceholderPrize } from 'assets/svg/prizes/placeholder.svg';
import { ReactComponent as DeliveryIcon } from 'assets/svg/delivery.svg';

// Components
import Card from 'components/Card/Card';
import Countdown from 'components/Timers/Countdown';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import Gallery from 'components/Sliders/GiveawaysGallery';
import { LinkIcon, StarsIcon, EntryArrowIcon } from 'components/Icons/Icons';
import OnePay from 'components/Membership/OnePay';
import TakeAPart from './components/TakeAPart';
import PrizesSlider from 'components/Sliders/Prizes';
import PrizeSpecs from 'components/Sliders/PrizeSpecs';

const PrizeItem = ({ indexPrize, title, firstAndLastName }) => {
  const width = useBreakpointValue({ base: '24px', md: '30px' });
  const height = useBreakpointValue({ base: '24px', md: '30px' });
  const w = useBreakpointValue({ base: '32px', md: '40px' });
  const h = useBreakpointValue({ base: '32px', md: '40px' });

  return (
    <Flex key={indexPrize} gap="12px" mb="16px" alignItems="center" zIndex="2">
      <Box borderRadius="8px" bg="rgba(9, 155, 255, 0.10)" w={w} h={h} p="5px">
        {indexPrize + 1 === 1 ? (
          <Prize1 style={{ width, height }} />
        ) : indexPrize + 1 === 2 ? (
          <Prize2 style={{ width, height }} />
        ) : indexPrize + 1 === 3 ? (
          <Prize3 style={{ width, height }} />
        ) : (
          <PlaceholderPrize style={{ width, height }} />
        )}
      </Box>

      <Box>
        <Text
          variant="header"
          fontSize={{ base: '20px', md: '24px' }}
          fontStyle="normal"
          mb={firstAndLastName ? '4px' : ''}
        >
          {indexPrize + 1}
          {indexPrize + 1 === 1
            ? 'st'
            : indexPrize + 1 === 2
            ? 'nd'
            : indexPrize + 1 === 3
            ? 'rd'
            : 'th'}{' '}
          Prize {title}
        </Text>
        {firstAndLastName && (
          <Text variant="text-lg" fontSize="20px" fontWeight="500">
            {firstAndLastName}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

const Giveaway = ({ userData }) => {
  const { id } = useParams();

  const colorDate = useColorModeValue('black', '#9FABCB');
  const dividerColor = useColorModeValue('#C8D3FF', '#252d47');

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const [images, setImages] = useState([]);
  const [activeSlidePrize, setActiveSlidePrize] = useState(0);
  const [viewFullInclusions, setViewFullInclusions] = useState(false);
  const isMobile = useBreakpointValue({ base: true, sm: false });
  const [isOneTimePaymentEnabled, setIsOneTimePaymentEnabled] = useState(false);

  const history = useHistory();

  const { data, isLoading } = useQuery({
    queryKey: ['oneGiveaway', id],
    queryFn: async () => {
      const response = await apiService.getGiveaway({ id });
      if (response.data.status === 'finished') {
        history.push('/');
      }

      const mainImage = response?.data?.image ? [response.data.image] : [];
      const galleryImages = Array.isArray(response?.data?.gallery)
        ? response.data.gallery.map((item) => item.image)
        : [];
      setImages([...mainImage, ...galleryImages]);
      setIsOneTimePaymentEnabled(response.data?.isOneTimePaymentEnabled);

      return response.data;
    },
    refetchOnWindowFocus: false,
  });

  return (
    <Box mt="-22px">
      <Breadcrumbs target="Giveaways" url="/giveaways" current="Details" />

      {!isLoading && (
        <>
          {images.length > 1 ? (
            <Box mt="32px" position="relative">
              <Gallery images={images} />
              {data.status === 'active' && (
                <Countdown
                  endDate={data?.endDate}
                  declareDate={data?.declareDate}
                  fontSize={{ base: '20px', md: '32px' }}
                />
              )}
            </Box>
          ) : (
            <Box mt="32px" position="relative">
              <Image
                src={data?.image ?? placeholderGiveaways}
                alt="Image giveaway"
                borderRadius="16px"
                w="100%"
                maxH="635px"
                border="1px solid #2ABDFD"
                boxShadow={
                  isDark
                    ? '0px 1px 22.4px 0px rgba(9, 155, 255, 0.60)'
                    : 'unset'
                }
                h={{ base: '224px', md: '100%' }}
                objectFit="cover"
              />

              {data.status === 'active' && (
                <Countdown
                  endDate={data?.endDate}
                  declareDate={data?.declareDate}
                  fontSize={{ base: '20px', md: '32px' }}
                />
              )}
            </Box>
          )}

          {data.status === 'active' && data?.entries > 0 && <TakeAPart />}

          {userData && data.status === 'active' && (
            <Text
              mt="32px"
              mb="12px"
              variant="title-md"
              fontSize={{ base: '20px', md: '22px' }}
              borderRadius="full"
              p="2px 16px"
              width="max-content"
              bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
              fontStyle="italic"
            >
              YOUR ENTRIES: {data?.entries}
            </Text>
          )}

          {data?.stream && (
            <Box mt="28px">
              <Text variant="header-gradient" fontSize="20px">
                Live giveaway available on these platforms
              </Text>

              <Flex justifyContent="start" gap="16px" mt="16px">
                {data?.stream?.instagram && (
                  <a href={data?.stream?.instagram}>
                    <Box
                      p="16px"
                      borderRadius="full"
                      bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                    >
                      <LuInstagram fontSize="24px" color="white" />
                    </Box>
                  </a>
                )}

                {data?.stream?.twitter && (
                  <a href={data?.stream?.twitter}>
                    <Box
                      p="16px"
                      borderRadius="full"
                      bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                    >
                      <RiTwitterXFill fontSize="24px" color="white" />
                    </Box>
                  </a>
                )}

                {data?.stream?.facebook && (
                  <a href={data?.stream?.facebook}>
                    <Box
                      p="16px"
                      borderRadius="full"
                      bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                    >
                      <MdFacebook fontSize="24px" color="white" />
                    </Box>
                  </a>
                )}

                {data?.stream?.youtube && (
                  <a href={data?.stream?.youtube}>
                    <Box
                      p="16px"
                      borderRadius="full"
                      bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                    >
                      <FaYoutube fontSize="24px" color="white" />
                    </Box>
                  </a>
                )}

                {data?.stream?.tiktok && (
                  <a href={data?.stream?.tiktok}>
                    <Box
                      p="16px"
                      borderRadius="full"
                      bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                    >
                      <FaTiktok fontSize="24px" color="white" />
                    </Box>
                  </a>
                )}

                {data?.stream?.twitch && (
                  <a href={data?.stream?.twitch}>
                    <Box
                      p="16px"
                      borderRadius="full"
                      bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                    >
                      <FaTwitch fontSize="24px" color="white" />
                    </Box>
                  </a>
                )}

                {data?.stream?.other && (
                  <a href={data?.stream?.other}>
                    <Box
                      p="16px"
                      borderRadius="full"
                      bg="linear-gradient(180deg, #2ABDFD 0%, #0497FD 100%)"
                    >
                      <LinkIcon fontSize="23px" color="white" />
                    </Box>
                  </a>
                )}
              </Flex>
            </Box>
          )}

          <Box mt="32px">
            <Text
              variant="header"
              fontSize={{ base: '36px', md: '40px', lg: '48px' }}
            >
              {data?.title}
            </Text>
            <Text
              variant="text-sm"
              fontSize={{ base: '18px', md: '24px' }}
              fontWeight="500"
              color={colorDate}
              mt="8px"
              isTruncated
            >
              {data?.status !== 'active'
                ? formatDateTime(data?.endDate)
                : parseFullDate(data?.declareDate)}{' '}
              {data?.status !== 'active' ? '- Event complete' : ''}
            </Text>
          </Box>

          {data?.prizes.length > 0 && (
            <Card
              padding="16px 16px 0px 16px"
              overflow="hidden"
              position="relative"
              mt="40px"
              pb={isMobile ? '100px' : '0'}
            >
              {data.prizes.map((prize, indexPrize) => (
                <PrizeItem
                  key={indexPrize}
                  indexPrize={indexPrize}
                  title={prize?.title}
                  firstAndLastName={
                    prize?.winner
                      ? `${prize.winner.firstName} ${prize.winner.lastName}`
                      : ''
                  }
                />
              ))}

              <Box
                position="absolute"
                top={isMobile ? 'unset' : '0px'}
                bottom={isMobile ? '-140px' : 'unset'}
                right="0"
                zIndex="1"
              >
                <Box
                  bg="rgba(64, 155, 255, 0.82)"
                  filter="blur(70px)"
                  w="240px"
                  h="240px"
                  borderRadius="full"
                  opacity={isDark ? '1' : '0'}
                />
                {isDark ? (
                  <Lines
                    className="abstract-lines"
                    style={{
                      zIndex: 1,
                      left: 'unset',
                      right: '0',
                      top: '-50%',
                      transform: 'scale(1) rotate(266deg)',
                    }}
                  />
                ) : (
                  <LinesWhite
                    className="abstract-lines"
                    style={{
                      zIndex: 1,
                      left: 'unset',
                      right: '0',
                      top: '-50%',
                      transform: 'scale(1) rotate(266deg)',
                      opacity: 0.3,
                    }}
                  />
                )}

                <Image
                  src={prizesImage}
                  alt="prizesImage"
                  bottom="-10%"
                  maxW="330px"
                  position="absolute"
                />
              </Box>
            </Card>
          )}

          {data?.prizes.length > 0 && (
            <Grid
              mt="18px"
              alignItems="center"
              templateColumns={{ base: '1fr', lg: '0.3fr 1fr 0.3fr' }}
              gap="10px"
              templateRows="1fr"
            >
              <Divider
                position="relative"
                mt={{ base: '16px', md: '55px' }}
                zIndex="9"
                borderColor="unset"
                borderBottomColor={dividerColor}
                borderBottomWidth="1px"
              />
              <PrizesSlider
                prizes={data?.prizes}
                activeSlidePrize={activeSlidePrize}
                setActiveSlidePrize={setActiveSlidePrize}
              />
              <Divider
                position="relative"
                mt={{ base: '16px', md: '55px' }}
                zIndex="9"
                borderColor="unset"
                borderBottomColor={dividerColor}
                borderBottomWidth="1px"
              />
            </Grid>
          )}

          {data?.prizes[activeSlidePrize]?.delivery && (
            <Flex gap="12px" mt={{ base: '16px', md: '40px' }}>
              <Box
                w="40px"
                height="40px"
                justifyContent="center"
                alignItems="center"
                p="5px"
                borderRadius="10px"
                bg="rgba(9, 155, 255, 0.10)"
              >
                <DeliveryIcon fontSize="30px" />
              </Box>

              <Box>
                <Text
                  variant="header"
                  fontSize={{ base: '20px', md: '24px' }}
                  fontStyle="normal"
                >
                  Delivery
                </Text>
                <Text mt="10px">
                  {data?.prizes[activeSlidePrize]?.delivery}
                </Text>
              </Box>
            </Flex>
          )}

          {data?.prizes[activeSlidePrize]?.cards?.length > 0 && (
            <Box mt="40px" overflow="hidden">
              <Text
                variant="title-lg"
                fontSize={{ base: '24px', md: '32px' }}
                fontWeight="800"
                letterSpacing="-1px"
                textTransform="uppercase"
              >
                Prize information
              </Text>
              <PrizeSpecs items={data?.prizes[activeSlidePrize]?.cards} />
            </Box>
          )}

          {data?.prizes[activeSlidePrize]?.specs && (
            <>
              <Box
                mt="40px"
                overflow="hidden"
                h={viewFullInclusions ? '100%' : '0'}
                templateColumns={{
                  sm: '1fr',
                  md: '1fr 1fr',
                  lg: '1fr 1fr 1fr',
                }}
                gap="24px"
                templateRows="1fr"
                mb={viewFullInclusions ? '40px' : '0'}
                className="html-component"
                dangerouslySetInnerHTML={{
                  __html: data?.prizes[activeSlidePrize]?.specs,
                }}
              />

              <Flex gap="8px" alignItems="center">
                <Divider
                  position="relative"
                  zIndex="9"
                  borderColor="unset"
                  borderBottomColor={dividerColor}
                  borderBottomWidth="1px"
                />
                <Flex
                  gap="5px"
                  pt="2px"
                  cursor="pointer"
                  onClick={() => setViewFullInclusions(!viewFullInclusions)}
                >
                  <Text color="#099BFF" whiteSpace="nowrap" fontWeight="500">
                    View Full Specs
                  </Text>
                  <EntryArrowIcon
                    fontSize="24px"
                    mt="-3px"
                    transform={
                      viewFullInclusions ? 'rotate(0deg)' : 'rotate(180deg)'
                    }
                  />
                </Flex>
                <Divider
                  position="relative"
                  zIndex="9"
                  borderColor="unset"
                  borderBottomColor={dividerColor}
                  borderBottomWidth="1px"
                />
              </Flex>
            </>
          )}

          <Grid
            alignItems="center"
            mt="40px"
            templateColumns={{ sm: '1fr', md: '0.8fr 1fr' }}
            gap="90px"
            rowGap="0"
            templateRows="1fr"
          >
            <Text>{data?.description}</Text>

            {data?.description && (
              <Box position="relative">
                <Image
                  src={descriptionImage}
                  alt="Description Image"
                  maxW={{ base: '100%', md: '576px' }}
                  w="100%"
                />
                <Box
                  className="abstract-grad"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%) scale(1.5)"
                />
              </Box>
            )}
          </Grid>

          {isOneTimePaymentEnabled && !data?.promotionLink && (
            <>
              <Box
                textAlign="center"
                mb="36px"
                mt={{ base: '0px', md: '84px' }}
                position="relative"
              >
                <Flex
                  alignItems="center"
                  gap="12px"
                  m="16px 0 17px 0"
                  w="100%"
                  justifyContent="center"
                >
                  <StarsIcon
                    fontSize="32px"
                    display={{ base: 'none', sm: 'block' }}
                  />
                  <Box
                    borderRadius={{ base: '8px', md: '16px' }}
                    p="6px 16px"
                    mt="-6px"
                    background="white"
                  >
                    {isDark && (
                      <Box
                        className="abstract-grad"
                        top="50%"
                        left="50%"
                        transform="translate(-50%, -50%) scale(1.5)"
                      />
                    )}
                    <Text
                      variant="header-gradient"
                      fontSize={{ base: '36px', md: '40px', lg: '48px' }}
                    >
                      Get More Entries
                    </Text>
                  </Box>
                  <StarsIcon
                    fontSize="32px"
                    display={{ base: 'none', sm: 'block' }}
                  />
                </Flex>
                <Text
                  variant="header-gradient"
                  fontSize={{ base: '36px', md: '40px', lg: '48px' }}
                >
                  to Join the Giveaways!
                </Text>
              </Box>
              <OnePay
                giveaway={data}
                setIsOneTimePaymentEnabled={setIsOneTimePaymentEnabled}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default Giveaway;
